import { type ReactNode, useState } from 'react'
import { isEmpty, isNotDefined, isNotEmpty, type LocationAutocompleteResult } from '@terros/common'
import { Combobox, Loader, TextInput, rem, useCombobox } from '@mantine/core'
import { useDebounce } from '@hooks'
import { useAutocomplete, useTranslation } from '@front/model'
import { MaterialSymbolIcon, TButton } from '@components'
import { type CompanyCardFormProps } from './CompanyCardFields'

export const GoogleBusinessProfileField = ({
  form,
  placeLabel,
}: CompanyCardFormProps & { placeLabel?: string }): ReactNode => {
  const { t } = useTranslation()
  const isConnected = isNotEmpty(form.values.placeId)
  const [search, setSearch] = useState('')
  const [selectedBusiness, setSelectedBusiness] = useState<LocationAutocompleteResult | undefined>()
  const debouncedSearch = useDebounce(search.trim(), 300)
  const query = !isConnected && debouncedSearch.length >= 2 ? debouncedSearch : ''
  const { data: suggestions = [], error, isFetching } = useAutocomplete({ query, provider: 'google' })
  const isSearching = isFetching || search.trim() !== debouncedSearch
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  function validateReviewFields(): void {
    form.validateField('placeId')
    form.validateField('teamInboxEmail')
    form.validateField('starThreshold')
  }

  function selectBusiness(placeId: string): void {
    const suggestion = suggestions.find((option) => option.placeId === placeId)
    if (isNotDefined(suggestion)) return
    setSearch(suggestion.text)
    setSelectedBusiness(suggestion)
    form.setFieldValue('placeId', suggestion.placeId)
    validateReviewFields()
    combobox.closeDropdown()
  }

  function disconnectBusiness(): void {
    setSearch('')
    setSelectedBusiness(undefined)
    form.setFieldValue('placeId', '')
    validateReviewFields()
    combobox.closeDropdown()
  }

  const selectedBusinessName = selectedBusiness?.placeId === form.values.placeId ? selectedBusiness?.text : undefined
  const connectedBusinessLabel = selectedBusinessName ?? placeLabel
  const inputValue = isConnected ? (connectedBusinessLabel ?? t('Connected business')) : search
  const searchRightSection = isSearching ? <Loader size='xs' /> : undefined
  const rightSection = isConnected ? (
    <TButton icon='link_off' size='xs' variant='ghost' onClick={disconnectBusiness}>
      {t('Disconnect')}
    </TButton>
  ) : (
    searchRightSection
  )

  return (
    <Combobox middlewares={{ flip: false }} position='bottom-start' store={combobox} onOptionSubmit={selectBusiness}>
      <Combobox.Target>
        <TextInput
          aria-required
          description={t('Select your business to show its Google rating and reviews on the card.')}
          disabled={isConnected}
          error={form.errors.placeId}
          label={t('Google Business Profile')}
          leftSection={<MaterialSymbolIcon icon='search' size={14} />}
          placeholder={t('Search by business name or address')}
          withAsterisk
          rightSection={rightSection}
          rightSectionPointerEvents={isConnected ? 'all' : 'none'}
          rightSectionWidth={isConnected ? 136 : undefined}
          value={inputValue}
          onBlur={() => combobox.closeDropdown()}
          onChange={(event) => {
            const updatedSearch = event.currentTarget.value
            setSearch(updatedSearch)
            if (updatedSearch.trim().length < 2) combobox.closeDropdown()
            else combobox.openDropdown()
          }}
          onFocus={() => {
            if (search.trim().length >= 2) combobox.openDropdown()
          }}
        />
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options mah={rem(320)} style={{ overflowY: 'auto' }}>
          {suggestions.map((suggestion) => (
            <Combobox.Option key={suggestion.placeId} value={suggestion.placeId}>
              {suggestion.text}
            </Combobox.Option>
          ))}
          {isSearching && <Combobox.Empty>{t('Searching...')}</Combobox.Empty>}
          {!isSearching && isNotEmpty(error) && <Combobox.Empty>{t('Failed to load')}</Combobox.Empty>}
          {!isSearching && isEmpty(error) && isNotEmpty(query) && isEmpty(suggestions) && (
            <Combobox.Empty>{t('No businesses found')}</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
